import { useState, useEffect, useRef } from 'react';
import { searchClients, getClientTeamPlan } from '../../services/clientService';
import { checkCollision, autoFill } from '../../services/callService';
import type { CollisionResult } from '../../services/callService';
import { searchProperties, getPropertyById } from '../../services/propertyService';
import { searchCallReports } from '../../services/callReportService';
import { createRetellCall } from '../../services/retellService';
import { searchCallPhoneNumbers } from '../../services/callPhoneNumberService';
import { searchCallVoices } from '../../services/callVoiceService';
import { searchAIAgents } from '../../services/aiAgentService';
import type { Client } from '../../types/client';
import type { Property } from '../../types/property';
import type { RetellCallReport } from '../../types/callReport';
import type { RetellAIAgent } from '../../types/aiAgent';
import type { CallPhoneNumber } from '../../types/callPhoneNumber';
import type { CallVoice } from '../../types/callVoice';

interface CreateCallModalProps {
  clientId?: string;
  propertyId?: string;
  clientFilter?: Record<string, unknown>;
  onClose: () => void;
  onSuccess: (callId: string) => void;
  onError?: () => void;
}

const US_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
];

const TEXT_FIELDS = [
  { key: 'toNumber', label: 'To Number' },
  { key: 'agent_name', label: 'Agent Name' },
] as const;

function autoSelect<T>(items: T[], getId: (item: T) => string, setter: (id: string) => void) {
  if (items.length === 1) setter(getId(items[0]));
}

export function CreateCallModal({ clientId: fixedClientId, propertyId: fixedPropertyId, clientFilter, onClose, onSuccess, onError }: CreateCallModalProps) {
  const [clients, setClients] = useState<Client[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [agents, setAgents] = useState<RetellAIAgent[]>([]);
  const [phoneNumbers, setPhoneNumbers] = useState<CallPhoneNumber[]>([]);
  const [voices, setVoices] = useState<CallVoice[]>([]);
  const [callReports, setCallReports] = useState<RetellCallReport[]>([]);

  const [clientId, setClientId] = useState(fixedClientId || '');
  const [propertyId, setPropertyId] = useState(fixedPropertyId || '');
  const [retellAgentId, setRetellAgentId] = useState('');
  const [fromNumber, setFromNumber] = useState('');
  const [voiceId, setVoiceId] = useState('');
  const [retellCallReportId, setRetellCallReportId] = useState('');
  const [timezone, setTimezone] = useState('America/New_York');
  const [textValues, setTextValues] = useState<Record<string, string>>(
    Object.fromEntries(TEXT_FIELDS.map((f) => [f.key, f.key === 'toNumber' ? '+' : ''])),
  );
  const [contextValues, setContextValues] = useState<Record<string, string>>({});

  const [callMode, setCallMode] = useState<'on-demand' | 'scheduled'>('on-demand');
  const [callDate, setCallDate] = useState('');
  const [callTime, setCallTime] = useState('');
  const [callDateTimezone, setCallDateTimezone] = useState('');
  const [voiceCollisions, setVoiceCollisions] = useState<CollisionResult[]>([]);

  const [planAgentId, setPlanAgentId] = useState<string | null>(null);
  const [fromNumberCollisions, setFromNumberCollisions] = useState<CollisionResult[]>([]);
  const [agentNameCollisions, setAgentNameCollisions] = useState<CollisionResult[]>([]);
  const [emailCollisions, setEmailCollisions] = useState<CollisionResult[]>([]);
  const [showCollisionList, setShowCollisionList] = useState<{ title: string; items: CollisionResult[] } | null>(null);
  const [submitCollisionConfirm, setSubmitCollisionConfirm] = useState<{
    fromNumber: CollisionResult[];
    agentName: CollisionResult[];
    voice: CollisionResult[];
    email: CollisionResult[];
  } | null>(null);
  const [autoFilling, setAutoFilling] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Resolve clientId from property when fixedPropertyId is set without fixedClientId
  useEffect(() => {
    if (fixedPropertyId && !fixedClientId) {
      getPropertyById(fixedPropertyId).then((prop) => {
        if (prop.clientId) setClientId(prop.clientId);
      }).catch(() => {});
    }
  }, [fixedPropertyId, fixedClientId]);

  // Fetch clients, agents, phone numbers, voices on mount
  useEffect(() => {
    Promise.all([
      searchClients(clientFilter || {}, { pageSize: 1000, orderBy: 'name', order: 'asc' }),
      searchAIAgents({ active: true }, { pageSize: 1000, orderBy: 'name', order: 'asc' }),
      searchCallPhoneNumbers({ status: 'active' }, { pageSize: 1000 }),
      searchCallVoices({ status: 'active' }, { pageSize: 1000 }),
    ])
      .then(([cls, ags, phones, vcs]) => {
        setClients(cls);
        if (!fixedClientId) autoSelect(cls, (c) => c.id, setClientId);
        setAgents(ags);
        setPhoneNumbers(phones);
        setVoices(vcs);
        autoSelect(ags, (a) => a.retellAgentId, setRetellAgentId);
        autoSelect(phones, (p) => p.phoneNumber, setFromNumber);
        const adrian = vcs.find((v) => v.voiceName === 'Adrian');
        if (adrian) {
          setVoiceId(adrian.voiceId);
        } else {
          autoSelect(vcs, (v) => v.voiceId, setVoiceId);
        }
      })
      .catch(() => setError('Failed to load form data'))
      .finally(() => setFetching(false));
  }, []);

  // Fetch client team plan to check for pinned agent
  useEffect(() => {
    if (!clientId) { setPlanAgentId(null); return; }
    getClientTeamPlan(clientId).then((resp) => {
      const aid = resp?.activePlan?.agentId || null;
      setPlanAgentId(aid);
      if (aid) setRetellAgentId(aid);
    }).catch(() => setPlanAgentId(null));
  }, [clientId]);

  // Fetch properties when client changes
  useEffect(() => {
    if (!clientId) {
      setProperties([]);
      return;
    }
    if (!fixedPropertyId) setPropertyId('');
    searchProperties({ clientId }, { pageSize: 1000, orderBy: 'name', order: 'asc' })
      .then((props) => {
        setProperties(props);
        if (!fixedPropertyId) autoSelect(props, (p) => p.id, setPropertyId);
      })
      .catch(() => setProperties([]));
  }, [clientId, fixedPropertyId]);

  // Default toNumber from selected property
  useEffect(() => {
    const selected = properties.find((p) => p.id === propertyId);
    if (selected?.toNumber) {
      setTextValues((prev) => ({ ...prev, toNumber: selected.toNumber! }));
    }
  }, [propertyId, properties]);

  // Fetch call reports when agent changes
  useEffect(() => {
    if (!retellAgentId) {
      setCallReports([]);
      setRetellCallReportId('');
      return;
    }
    searchCallReports({ agentId: retellAgentId }, { pageSize: 1000 })
      .then((reports) => {
        setCallReports(reports);
        setRetellCallReportId('');
        autoSelect(reports, (r) => r.id, setRetellCallReportId);
      })
      .catch(() => setCallReports([]));
  }, [retellAgentId]);

  // Initialize context values when agent changes (context data now lives on agent)
  useEffect(() => {
    const selected = agents.find((a) => a.retellAgentId === retellAgentId);
    setContextValues(
      Object.fromEntries((selected?.contextData || []).map((f) => [f.key, f.default || ''])),
    );
  }, [retellAgentId, agents]);

  // Debounced collision check
  const collisionTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    setFromNumberCollisions([]);
    setAgentNameCollisions([]);
    setVoiceCollisions([]);
    setEmailCollisions([]);
    if (!clientId || !propertyId) return;
    const fn = fromNumber;
    const an = textValues.agent_name;
    const vi = voiceId;
    const em = contextValues.email_address || '';
    if (!fn && !an && !vi && !em) return;

    if (collisionTimer.current) clearTimeout(collisionTimer.current);
    collisionTimer.current = setTimeout(() => {
      checkCollision({ clientId, propertyId, fromNumber: fn, agentName: an, voiceId: vi, email: em }).then((resp) => {
        setFromNumberCollisions(resp.fromNumberCollisions || []);
        setAgentNameCollisions(resp.agentNameCollisions || []);
        setVoiceCollisions(resp.voiceCollisions || []);
        setEmailCollisions(resp.emailCollisions || []);
      }).catch(() => {});
    }, 500);

    return () => { if (collisionTimer.current) clearTimeout(collisionTimer.current); };
  }, [clientId, propertyId, fromNumber, textValues.agent_name, voiceId, contextValues.email_address]);

  // Default timezone from property
  useEffect(() => {
    const selected = properties.find((p) => p.id === propertyId);
    if (selected?.timezone) {
      setCallDateTimezone(selected.timezone);
      setTimezone(selected.timezone);
    }
  }, [propertyId, properties]);

  // Auto-fill handler
  const handleAutoFill = async () => {
    if (!clientId) return;
    setAutoFilling(true);
    try {
      const resp = await autoFill(clientId, propertyId || undefined);
      if (resp.propertyId && !propertyId) setPropertyId(resp.propertyId);
      if (resp.retellAgentId) setRetellAgentId(resp.retellAgentId);
      if (resp.fromNumber) setFromNumber(resp.fromNumber);
      if (resp.voiceId) setVoiceId(resp.voiceId);
      if (resp.retellCallReportId) setRetellCallReportId(resp.retellCallReportId);
      if (resp.timezone) {
        setTimezone(resp.timezone);
        setCallDateTimezone(resp.timezone);
      }
      if (resp.inputVars) {
        setContextValues(prev => ({ ...prev, ...resp.inputVars }));
      }
    } catch {
      // error handled by apiService
    } finally {
      setAutoFilling(false);
    }
  };

  const selectedAgent = agents.find((a) => a.retellAgentId === retellAgentId);
  const allTextFilled = TEXT_FIELDS.every((f) => textValues[f.key].trim());
  const allContextFilled = selectedAgent?.contextData?.every((f) => contextValues[f.key]?.trim()) ?? true;
  const scheduledValid = callMode === 'scheduled' ? (callDate && callTime && callDateTimezone) : true;
  const canSubmit = propertyId && retellAgentId && fromNumber && voiceId && retellCallReportId && timezone && allTextFilled && allContextFilled && scheduledValid;

  const executeCreateCall = async () => {
    setError('');
    setLoading(true);
    try {
      const data: Record<string, string>[] = [
        ...Object.entries(textValues)
          .filter(([k]) => k !== 'toNumber')
          .map(([k, v]) => ({ [k]: v })),
        { contact_ai_agent_name: textValues.agent_name },
        ...Object.entries(contextValues).map(([k, v]) => ({ [k]: v })),
      ];

      const result = await createRetellCall({
        fromNumber,
        toNumber: textValues.toNumber,
        retellAgentId,
        propertyId,
        timezone,
        voiceId,
        retellCallReportId: retellCallReportId || null,
        data,
        ...(callMode === 'scheduled' ? {
          callDate: `${callDate}T${callTime}:00`,
          callDateTimezone,
          callType: 'scheduled' as const,
        } : {}),
      });
      onSuccess(result.id);
    } catch {
      setError('Failed to create call');
      onError?.();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    const e164Regex = /^\+1[2-9]\d{9}$/;
    if (!e164Regex.test(textValues.toNumber)) {
      setError('To Number must be a valid NANP E.164 number (e.g. +13031231234)');
      return;
    }

    setError('');
    setLoading(true);
    let resp;
    try {
      resp = await checkCollision({
        clientId,
        propertyId,
        fromNumber,
        agentName: textValues.agent_name,
        voiceId,
        email: contextValues.email_address || '',
      });
    } catch {
      // If collision check fails, proceed without blocking — original behavior.
      setLoading(false);
      executeCreateCall();
      return;
    }
    setFromNumberCollisions(resp.fromNumberCollisions || []);
    setAgentNameCollisions(resp.agentNameCollisions || []);
    setVoiceCollisions(resp.voiceCollisions || []);
    setEmailCollisions(resp.emailCollisions || []);
    setLoading(false);

    const hasAny = (resp.fromNumberCollisions?.length || 0) + (resp.agentNameCollisions?.length || 0) + (resp.voiceCollisions?.length || 0) + (resp.emailCollisions?.length || 0) > 0;
    if (hasAny) {
      setSubmitCollisionConfirm({
        fromNumber: resp.fromNumberCollisions || [],
        agentName: resp.agentNameCollisions || [],
        voice: resp.voiceCollisions || [],
        email: resp.emailCollisions || [],
      });
      return;
    }
    executeCreateCall();
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        <form onSubmit={handleSubmit} className="flex flex-col max-h-[90vh]">
          <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Create Call</h2>
              <button
                type="button"
                onClick={handleAutoFill}
                disabled={!clientId || autoFilling}
                className="px-3 py-1.5 text-sm font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {autoFilling ? 'Filling...' : 'Auto Fill'}
              </button>
              <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                <button type="button" onClick={() => setCallMode('on-demand')} className={`px-3 py-1.5 text-sm font-medium transition-colors ${callMode === 'on-demand' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>On-Demand</button>
                <button type="button" onClick={() => setCallMode('scheduled')} className={`px-3 py-1.5 text-sm font-medium transition-colors ${callMode === 'scheduled' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>Scheduled</button>
              </div>
            </div>
            {fetching ? (
              <p className="text-sm text-gray-400">Loading...</p>
            ) : (
              <div className="space-y-4">
                {/* Client */}
                <div>
                  <label className={labelClass}>Client *</label>
                  <select
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    required
                    disabled={!!fixedClientId || !!fixedPropertyId}
                    className={inputClass}
                  >
                    <option value="">Select client</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>

                {/* Property */}
                <div>
                  <label className={labelClass}>Property *</label>
                  <select
                    value={propertyId}
                    onChange={(e) => setPropertyId(e.target.value)}
                    required
                    disabled={!!fixedPropertyId || !clientId}
                    className={inputClass}
                  >
                    <option value="">Select property</option>
                    {properties.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>

                {(() => {
                  const selectedProp = properties.find(p => p.id === propertyId);
                  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
                  const isClosed = selectedProp?.closedDaysWeek?.includes(today);
                  return isClosed ? (
                    <p className="text-red-600 text-sm font-bold">This property is closed: {selectedProp!.closedDaysWeek!.join(', ')}.</p>
                  ) : null;
                })()}

                {/* Agent */}
                <div>
                  <label className={labelClass}>Agent *</label>
                  <select
                    value={retellAgentId}
                    onChange={(e) => setRetellAgentId(e.target.value)}
                    required
                    disabled={!!planAgentId}
                    className={`${inputClass}${planAgentId ? ' bg-gray-100 cursor-not-allowed' : ''}`}
                  >
                    <option value="">Select agent</option>
                    {(planAgentId ? agents.filter(a => a.retellAgentId === planAgentId) : agents).map((a) => (
                      <option key={a.retellAgentId} value={a.retellAgentId}>{a.name}</option>
                    ))}
                  </select>
                </div>

                {/* From Number */}
                <div>
                  <label className={labelClass}>From Number *</label>
                  <select
                    value={fromNumber}
                    onChange={(e) => setFromNumber(e.target.value)}
                    required
                    className={inputClass}
                  >
                    <option value="">Select number</option>
                    {phoneNumbers.map((n) => (
                      <option key={n.id} value={n.phoneNumber}>{n.phoneNumber}</option>
                    ))}
                  </select>
                  {fromNumberCollisions.length > 0 && (() => {
                    const latest = fromNumberCollisions[0];
                    const date = new Date(latest.date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
                    return (
                      <p className="text-red-600 text-xs font-bold mt-1">
                        This from number was used at {latest.propertyName || 'unknown'} on {date}
                        {fromNumberCollisions.length > 1 && (
                          <button type="button" onClick={() => setShowCollisionList({ title: 'From Number Collisions', items: fromNumberCollisions })} className="ml-1 underline">({fromNumberCollisions.length - 1} more)</button>
                        )}
                      </p>
                    );
                  })()}
                </div>

                {/* Voice */}
                <div>
                  <label className={labelClass}>Select Voice *</label>
                  <select
                    value={voiceId}
                    onChange={(e) => setVoiceId(e.target.value)}
                    required
                    className={inputClass}
                  >
                    <option value="">Select voice</option>
                    {voices.map((v) => (
                      <option key={v.id} value={v.voiceId}>{v.name || v.voiceName}</option>
                    ))}
                  </select>
                  {voiceCollisions.length > 0 && (() => {
                    const latest = voiceCollisions[0];
                    const date = new Date(latest.date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
                    return (
                      <p className="text-red-600 text-xs font-bold mt-1">
                        This voice was used at {latest.propertyName || 'unknown'} on {date}
                        {voiceCollisions.length > 1 && (
                          <button type="button" onClick={() => setShowCollisionList({ title: 'Voice Collisions', items: voiceCollisions })} className="ml-1 underline">({voiceCollisions.length - 1} more)</button>
                        )}
                      </p>
                    );
                  })()}
                </div>

                {/* Call Report (dependent on agent) */}
                <div>
                  <label className={labelClass}>Call Report *</label>
                  <select
                    value={retellCallReportId}
                    onChange={(e) => setRetellCallReportId(e.target.value)}
                    required
                    disabled={!retellAgentId}
                    className={inputClass}
                  >
                    <option value="">Select call report</option>
                    {callReports.map((r) => (
                      <option key={r.id} value={r.id}>{r.name || r.id}</option>
                    ))}
                  </select>
                </div>

                {/* Timezone */}
                <div>
                  <label className={labelClass}>Timezone *</label>
                  <select
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    required
                    className={inputClass}
                  >
                    {US_TIMEZONES.map((tz) => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                </div>

                {/* To Number */}
                {(() => {
                  const phoneValue = textValues.toNumber;
                  const phoneInvalid = phoneValue.length > 0 && !/^\+1[2-9]\d{9}$/.test(phoneValue);
                  return (
                    <div>
                      <label className={labelClass}>To Number *</label>
                      <input
                        type="text"
                        value={phoneValue}
                        onChange={(e) => setTextValues((prev) => ({ ...prev, toNumber: e.target.value }))}
                        required
                        placeholder="+13031231234"
                        className={`${inputClass}${phoneInvalid ? ' border-red-400' : ''}`}
                      />
                      {phoneInvalid && (
                        <p className="text-red-500 text-xs mt-1">Must be E.164 format (e.g. +13031231234)</p>
                      )}
                    </div>
                  );
                })()}

                {/* Scheduled: Call Date */}
                {callMode === 'scheduled' && (
                  <div>
                    <label className={labelClass}>Call Date *</label>
                    <div className="flex items-center gap-2">
                      <input type="date" value={callDate} onChange={(e) => setCallDate(e.target.value)} required className={`${inputClass} flex-1`} />
                      <input type="time" value={callTime} onChange={(e) => setCallTime(e.target.value)} required className={`${inputClass} w-28`} />
                      <select value={callDateTimezone} onChange={(e) => setCallDateTimezone(e.target.value)} required className={`${inputClass} w-40`}>
                        <option value="">TZ</option>
                        <option value="Pacific/Honolulu">Hawaii</option>
                        <option value="America/Anchorage">Alaska</option>
                        <option value="America/Los_Angeles">Pacific</option>
                        <option value="America/Denver">Mountain</option>
                        <option value="America/Chicago">Central</option>
                        <option value="America/New_York">Eastern</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Dynamic Inputs section */}
                <div className="border border-gray-200 rounded-lg p-4 space-y-4">
                  <p className="text-sm font-bold text-gray-900">Dynamic Inputs</p>

                  {/* Agent Name */}
                  <div>
                    <label className={labelClass}>Agent Name *</label>
                    <input
                      type="text"
                      value={textValues.agent_name}
                      onChange={(e) => setTextValues((prev) => ({ ...prev, agent_name: e.target.value }))}
                      required
                      className={inputClass}
                    />
                    {agentNameCollisions.length > 0 && (() => {
                      const latest = agentNameCollisions[0];
                      const date = new Date(latest.date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
                      return (
                        <p className="text-red-600 text-xs font-bold mt-1">
                          This agent name was used at {latest.propertyName || 'unknown'} on {date}
                          {agentNameCollisions.length > 1 && (
                            <button type="button" onClick={() => setShowCollisionList({ title: 'Agent Name Collisions', items: agentNameCollisions })} className="ml-1 underline">({agentNameCollisions.length - 1} more)</button>
                          )}
                        </p>
                      );
                    })()}
                  </div>

                  {/* Dynamic context data fields */}
                  {selectedAgent?.contextData?.map((f) => (
                    <div key={f.key}>
                      <label className={labelClass}>{f.label} *</label>
                      <input
                        type="text"
                        value={contextValues[f.key] || ''}
                        onChange={(e) => setContextValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
                        required
                        className={inputClass}
                      />
                      {f.key === 'email_address' && emailCollisions.length > 0 && (() => {
                        const latest = emailCollisions[0];
                        const date = new Date(latest.date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
                        return (
                          <p className="text-red-600 text-xs font-bold mt-1">
                            This email was used at {latest.propertyName || 'unknown'} on {date}
                            {emailCollisions.length > 1 && (
                              <button type="button" onClick={() => setShowCollisionList({ title: 'Email Collisions', items: emailCollisions })} className="ml-1 underline">({emailCollisions.length - 1} more)</button>
                            )}
                          </p>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
          </div>
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || fetching || !canSubmit}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (callMode === 'scheduled' ? 'Scheduling...' : 'Creating...') : (callMode === 'scheduled' ? 'Schedule Call' : 'Create Call')}
            </button>
          </div>
        </form>
      </div>
      {showCollisionList && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowCollisionList(null)} />
          <div className="relative bg-white rounded-xl shadow-lg w-full max-w-md mx-4 max-h-[70vh] flex flex-col">
            <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
              <h2 className="text-lg font-bold text-gray-900 mb-3">{showCollisionList.title}</h2>
              <div className="space-y-2">
                {showCollisionList.items.map((item, i) => (
                  <div key={i} className="text-sm text-gray-700 border-b border-gray-100 pb-2">
                    <span className="font-medium">{item.propertyName || 'Unknown'}</span> — {new Date(item.date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                    <div className="text-xs text-gray-500">From: {item.fromNumber} | Agent: {item.agentName || '—'}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end px-6 py-4 border-t border-gray-100">
              <button type="button" onClick={() => setShowCollisionList(null)} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Close</button>
            </div>
          </div>
        </div>
      )}
      {submitCollisionConfirm && (() => {
        const sections: { label: string; items: CollisionResult[] }[] = [
          { label: 'From Number', items: submitCollisionConfirm.fromNumber },
          { label: 'Agent Name', items: submitCollisionConfirm.agentName },
          { label: 'Voice', items: submitCollisionConfirm.voice },
          { label: 'Email', items: submitCollisionConfirm.email },
        ].filter((s) => s.items.length > 0);
        return (
          <div className="fixed inset-0 z-[60] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setSubmitCollisionConfirm(null)} />
            <div className="relative bg-white rounded-xl shadow-lg w-full max-w-md mx-4 max-h-[70vh] flex flex-col">
              <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
                <h2 className="text-lg font-bold text-gray-900 mb-2">Possible Collisions</h2>
                <p className="text-sm text-gray-600 mb-4">The following inputs were used in recent calls within this property's collision window. Create the call anyway?</p>
                <div className="space-y-4">
                  {sections.map((s) => (
                    <div key={s.label}>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{s.label}</p>
                      {s.items.slice(0, 3).map((item, i) => (
                        <div key={i} className="text-sm text-gray-700">
                          <span className="font-medium">{item.propertyName || 'Unknown'}</span> — {new Date(item.date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                        </div>
                      ))}
                      {s.items.length > 3 && (
                        <div className="text-xs text-gray-500">+{s.items.length - 3} more</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
                <button type="button" onClick={() => setSubmitCollisionConfirm(null)} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => { setSubmitCollisionConfirm(null); executeCreateCall(); }}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {callMode === 'scheduled' ? 'Schedule Anyway' : 'Create Anyway'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
